import { useState, useEffect, useRef } from 'react';
import toast from 'react-hot-toast';

// Avery tag insert: 2.25in x 3.5in (portrait). Canvas at 600 DPI for crisp printing.
const W = 1350;  // 2.25 * 600
const H = 2100;  // 3.5  * 600

// Brand colors (from the CPR4homes logo)
const GREEN_DARK = '#0D3B2E';
const GREEN_MID  = '#15543b';
const GOLD       = '#c9a227';
const GOLD_LIGHT = '#e8c75a';

function fitText(ctx, text, maxWidth, startSize, weight = 900, font = 'Arial Black, Arial') {
  let size = startSize;
  do {
    ctx.font = `${weight} ${size}px ${font}`;
    if (ctx.measureText(text).width <= maxWidth) break;
    size -= 4;
  } while (size > 20);
  return size;
}

function drawCover(ctx, img, x, y, w, h, focus) {
  // draw image covering the box (like CSS object-fit: cover)
  // focus 'top' anchors the crop to the top of the image (keeps heads visible)
  const ir = img.width / img.height, br = w / h;
  let sx = 0, sy = 0, sw = img.width, sh = img.height;
  if (ir > br) { sw = img.height * br; sx = (img.width - sw) / 2; }
  else { sh = img.width / br; sy = focus === 'top' ? 0 : (img.height - sh) / 2; }
  ctx.drawImage(img, sx, sy, sw, sh, x, y, w, h);
}

function rr(ctx, x, y, w, h, r) {
  // rounded-rect path (works in all browsers, no ctx.roundRect needed)
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

function greenGradient(ctx, x0, y0, x1, y1) {
  const g = ctx.createLinearGradient(x0, y0, x1, y1);
  g.addColorStop(0, GREEN_DARK);
  g.addColorStop(1, GREEN_MID);
  return g;
}

function drawFront(canvas, data, photoImg, logoImg) {
  const ctx = canvas.getContext('2d');
  ctx.clearRect(0, 0, W, H);

  // white card
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, W, H);

  // ---- top green header band with the logo ----
  const bandH = 400;
  ctx.fillStyle = greenGradient(ctx, 0, 0, 0, bandH);
  ctx.fillRect(0, 0, W, bandH);
  // gold line under the band
  ctx.fillStyle = GOLD;
  ctx.fillRect(0, bandH, W, 10);

  // logo: rounded square, gold frame, centered in the band
  const logoS = 330;
  const logoX = (W - logoS) / 2;
  const logoY = (bandH - logoS) / 2;
  if (logoImg) {
    ctx.save();
    rr(ctx, logoX, logoY, logoS, logoS, 28);
    ctx.clip();
    drawCover(ctx, logoImg, logoX, logoY, logoS, logoS);
    ctx.restore();
    ctx.strokeStyle = GOLD;
    ctx.lineWidth = 8;
    rr(ctx, logoX, logoY, logoS, logoS, 28);
    ctx.stroke();
  } else {
    ctx.strokeStyle = GOLD;
    ctx.lineWidth = 10;
    rr(ctx, logoX, logoY, logoS, logoS, 28);
    ctx.stroke();
    ctx.fillStyle = GOLD;
    ctx.font = '900 64px Arial Black, Arial';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('LOGO', W / 2, bandH / 2);
    ctx.textBaseline = 'alphabetic';
  }

  // ---- BIG photo ----
  const photoW = Math.round(W * 0.78);            // much larger than before
  const photoH = Math.round(photoW * 1.18);
  const photoX = Math.round((W - photoW) / 2);
  const photoY = bandH + 70;
  // gold frame
  ctx.fillStyle = GOLD;
  rr(ctx, photoX - 12, photoY - 12, photoW + 24, photoH + 24, 30);
  ctx.fill();
  // photo
  ctx.save();
  rr(ctx, photoX, photoY, photoW, photoH, 22);
  ctx.clip();
  ctx.fillStyle = '#e7ece9';
  ctx.fillRect(photoX, photoY, photoW, photoH);
  if (photoImg) drawCover(ctx, photoImg, photoX, photoY, photoW, photoH, 'top');
  else {
    ctx.fillStyle = '#8aa096';
    ctx.font = '600 60px Arial';
    ctx.textAlign = 'center';
    ctx.fillText('PHOTO', photoX + photoW / 2, photoY + photoH / 2);
  }
  ctx.restore();

  // ---- name / title / ID ----
  const cx = W / 2;
  const maxW = W - 140;
  let y = photoY + photoH + 110;

  const name = (data.name || 'EMPLOYEE NAME').toUpperCase();
  ctx.textAlign = 'center';
  ctx.fillStyle = GREEN_DARK;
  let lines = [name];
  // split to 2 lines only if it would otherwise get tiny
  if (name.split(/\s+/).length > 1 && fitText(ctx, name, maxW, 104) < 62) {
    const words = name.split(/\s+/);
    const mid = Math.ceil(words.length / 2);
    lines = [words.slice(0, mid).join(' '), words.slice(mid).join(' ')];
  }
  const nSize = Math.min(...lines.map(l => fitText(ctx, l, maxW, 104)));
  ctx.font = `900 ${nSize}px Arial Black, Arial`;
  lines.forEach(l => { ctx.fillText(l, cx, y); y += nSize + 14; });

  // gold pill with the job title
  const title = (data.title || 'JOB TITLE').toUpperCase();
  const tSize = fitText(ctx, title, maxW - 160, 58, 800, 'Arial');
  ctx.font = `800 ${tSize}px Arial`;
  const tW = ctx.measureText(title).width;
  const pillW = tW + 110, pillH = tSize + 50;
  y += 16;
  ctx.fillStyle = GOLD;
  rr(ctx, cx - pillW / 2, y - pillH / 2 - 8, pillW, pillH, pillH / 2);
  ctx.fill();
  ctx.fillStyle = GREEN_DARK;
  ctx.fillText(title, cx, y + tSize / 2 - 12);

  // ---- bottom green strip ----
  const stripH = 96;
  ctx.fillStyle = GOLD;
  ctx.fillRect(0, H - stripH - 8, W, 8);
  ctx.fillStyle = greenGradient(ctx, 0, H - stripH, 0, H);
  ctx.fillRect(0, H - stripH, W, stripH);
  ctx.fillStyle = GOLD_LIGHT;
  const site = data.website || 'CPR4homes.com';
  ctx.font = `800 ${fitText(ctx, site, W - 200, 46, 800, 'Arial')}px Arial`;
  ctx.fillText(site, cx, H - stripH / 2 + 14);
}

function drawBack(canvas, data, logoImg) {
  const ctx = canvas.getContext('2d');
  ctx.clearRect(0, 0, W, H);
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, W, H);

  // top green band + company name
  const bandH = 240;
  ctx.fillStyle = greenGradient(ctx, 0, 0, W, 0);
  ctx.fillRect(0, 0, W, bandH);
  ctx.fillStyle = GOLD;
  ctx.fillRect(0, bandH, W, 10);
  ctx.fillStyle = GOLD_LIGHT;
  ctx.textAlign = 'center';
  const comp = (data.company || 'CPR4HOMES').toUpperCase();
  ctx.font = `900 ${fitText(ctx, comp, W - 160, 86)}px Arial Black, Arial`;
  ctx.fillText(comp, W / 2, bandH / 2 + 30);

  let y = bandH + 190;
  ctx.fillStyle = GREEN_DARK;
  ctx.font = '800 56px Arial';
  ctx.fillText('IF FOUND, PLEASE RETURN TO:', W / 2, y);
  y += 110;

  ctx.font = '500 52px Arial';
  ctx.fillStyle = '#333';
  const lines = [data.address, data.phone && `Phone: ${data.phone}`, data.website].filter(Boolean);
  if (!lines.length) lines.push('Company contact info');
  for (const line of lines) {
    const max = W - 200;
    let words = String(line).split(' ');
    let cur = '';
    for (const w of words) {
      const test = cur ? cur + ' ' + w : w;
      if (ctx.measureText(test).width > max && cur) { ctx.fillText(cur, W / 2, y); y += 78; cur = w; }
      else cur = test;
    }
    if (cur) { ctx.fillText(cur, W / 2, y); y += 96; }
  }

  // custom back text
  if (data.backText) {
    y += 40;
    ctx.font = 'italic 46px Arial';
    ctx.fillStyle = '#555';
    const max = W - 220;
    let words = data.backText.split(' ');
    let cur = '';
    for (const w of words) {
      const test = cur ? cur + ' ' + w : w;
      if (ctx.measureText(test).width > max && cur) { ctx.fillText(cur, W / 2, y); y += 70; cur = w; }
      else cur = test;
    }
    if (cur) { ctx.fillText(cur, W / 2, y); y += 70; }
  }

  // logo (rounded square, gold frame) above the bottom strip
  const stripH = 150;
  const logoS = 260;
  const lx = (W - logoS) / 2, ly = H - stripH - logoS - 70;
  if (logoImg) {
    ctx.save();
    rr(ctx, lx, ly, logoS, logoS, 24);
    ctx.clip();
    drawCover(ctx, logoImg, lx, ly, logoS, logoS);
    ctx.restore();
    ctx.strokeStyle = GOLD;
    ctx.lineWidth = 7;
    rr(ctx, lx, ly, logoS, logoS, 24);
    ctx.stroke();
  }

  // bottom green strip
  ctx.fillStyle = GOLD;
  ctx.fillRect(0, H - stripH - 8, W, 8);
  ctx.fillStyle = greenGradient(ctx, 0, H - stripH, W, H);
  ctx.fillRect(0, H - stripH, W, stripH);
}

export default function IDCards() {
  const [data, setData] = useState({ name: '', title: '', company: 'CPR4homes', phone: '', address: '', website: 'CPR4homes.com', backText: 'This card is the property of the company. Use by anyone other than the named employee is prohibited.' });
  const [photoImg, setPhotoImg] = useState(null);
  const [logoImg, setLogoImg] = useState(null);
  const frontRef = useRef(null);
  const backRef = useRef(null);

  // auto-load the company logo from the site (no upload needed)
  useEffect(() => {
    const img = new Image();
    img.onload = () => setLogoImg(img);
    img.src = '/cpr-logo.jpeg';
  }, []);

  function loadImage(e, setter) {
    const file = e.target.files[0];
    if (!file) return;
    const img = new Image();
    img.onload = () => setter(img);
    img.onerror = () => toast.error('Could not read that image');
    img.src = URL.createObjectURL(file);
  }

  useEffect(() => {
    if (frontRef.current) drawFront(frontRef.current, data, photoImg, logoImg);
    if (backRef.current) drawBack(backRef.current, data, logoImg);
  }, [data, photoImg, logoImg]);

  function printCards() {
    const f = frontRef.current ? frontRef.current.toDataURL('image/png') : '';
    const b = backRef.current ? backRef.current.toDataURL('image/png') : '';
    if (!f && !b) { toast.error('Nothing to print yet'); return; }

    // Hidden iframe (not blocked by pop-up blockers, unlike window.open)
    const iframe = document.createElement('iframe');
    iframe.setAttribute('aria-hidden', 'true');
    iframe.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;';
    document.body.appendChild(iframe);

    const doc = iframe.contentWindow.document;
    doc.open();
    doc.write(`<!DOCTYPE html><html><head><title>id_card</title>
      <style>
        @page { size: 2.25in 3.5in; margin: 0; }
        * { margin: 0; padding: 0; box-sizing: border-box; }
        html, body { width: 2.25in; height: 3.5in; }
        .pg { width: 2.25in; height: 3.5in; page-break-after: always; break-after: page; overflow: hidden; display: block; }
        .pg:last-child { page-break-after: auto; break-after: auto; }
        .pg img { width: 2.25in; height: 3.5in; display: block; object-fit: cover; }
      </style></head><body>
      ${f ? `<div class="pg"><img src="${f}"></div>` : ''}
      ${b ? `<div class="pg"><img src="${b}"></div>` : ''}
      </body></html>`);
    doc.close();

    const cleanup = () => { try { document.body.removeChild(iframe); } catch (e) {} };
    const go = () => {
      try {
        iframe.contentWindow.focus();
        iframe.contentWindow.print();
      } catch (e) {
        toast.error('Could not open the print dialog');
      }
      setTimeout(cleanup, 1500);
    };

    // Print only once the card images have actually loaded
    const imgs = Array.from(doc.images);
    let pending = imgs.length;
    if (pending === 0) { setTimeout(go, 150); return; }
    imgs.forEach(img => {
      if (img.complete) { if (--pending === 0) go(); }
      else { img.onload = img.onerror = () => { if (--pending === 0) go(); }; }
    });
    // safety net in case an onload never fires
    setTimeout(() => { if (pending > 0) { pending = 0; go(); } }, 2000);
  }

  function download(side) {
    const canvas = side === 'front' ? frontRef.current : backRef.current;
    const a = document.createElement('a');
    a.download = `id-card-${(data.name || 'card').replace(/\s+/g, '_')}-${side}.png`;
    a.href = canvas.toDataURL('image/png');
    a.click();
  }

  const inp = { width: '100%', padding: '8px 10px', border: '1px solid #ccc', borderRadius: 7, fontSize: 14, marginBottom: 10 };
  const lbl = { fontSize: 12, color: '#666', fontWeight: 600, display: 'block', marginBottom: 4 };

  return (
    <>
      <div className="page-header">
        <h2>ID Card Maker</h2>
        <div style={{ fontSize: 13, color: '#888' }}>Avery tag insert 2.25" × 3.5" — fill in, preview, then print or download</div>
      </div>
      <div className="page-body">
        <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', alignItems: 'flex-start' }}>
          {/* form */}
          <div style={{ flex: '1 1 320px', maxWidth: 420, background: '#fff', borderRadius: 12, padding: 20, border: '1px solid #eee' }}>
            <label style={lbl}>Employee name</label>
            <input style={inp} value={data.name} onChange={e => setData(d => ({ ...d, name: e.target.value }))} placeholder="Benjamin Anderson" />
            <label style={lbl}>Job title</label>
            <input style={inp} value={data.title} onChange={e => setData(d => ({ ...d, title: e.target.value }))} placeholder="Sales Representative" />
            <label style={lbl}>Company name (back of card)</label>
            <input style={inp} value={data.company} onChange={e => setData(d => ({ ...d, company: e.target.value }))} />
            <label style={lbl}>Photo</label>
            <input type="file" accept="image/*" style={inp} onChange={e => loadImage(e, setPhotoImg)} />
            <label style={lbl}>Logo (already loaded automatically — upload only to replace it)</label>
            <input type="file" accept="image/*" style={inp} onChange={e => loadImage(e, setLogoImg)} />
            <hr style={{ border: 'none', borderTop: '1px solid #eee', margin: '14px 0' }} />
            <div style={{ fontSize: 13, fontWeight: 700, color: '#0D3B2E', marginBottom: 8 }}>Back of card</div>
            <label style={lbl}>Phone</label>
            <input style={inp} value={data.phone} onChange={e => setData(d => ({ ...d, phone: e.target.value }))} placeholder="(555) 123-4567" />
            <label style={lbl}>Address</label>
            <input style={inp} value={data.address} onChange={e => setData(d => ({ ...d, address: e.target.value }))} placeholder="123 Main St, City, CA" />
            <label style={lbl}>Website</label>
            <input style={inp} value={data.website} onChange={e => setData(d => ({ ...d, website: e.target.value }))} placeholder="CPR4homes.com" />
            <label style={lbl}>Back text</label>
            <textarea style={{ ...inp, minHeight: 70 }} value={data.backText} onChange={e => setData(d => ({ ...d, backText: e.target.value }))} />
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 6 }}>
              <button className="btn btn-primary" onClick={printCards}>🖨️ Print (front + back)</button>
              <button className="btn btn-outline" onClick={() => download('front')}>⬇ Front PNG</button>
              <button className="btn btn-outline" onClick={() => download('back')}>⬇ Back PNG</button>
            </div>
            <div style={{ fontSize: 11, color: '#999', marginTop: 10 }}>
              Print tip: the print dialog is pre-sized to the exact insert (2.25" × 3.5"), front on page 1 and back on page 2 — print page 1, flip, print page 2. Set printer scaling to 100% / "Actual size".
            </div>
          </div>

          {/* live preview */}
          <div style={{ flex: '1 1 380px', display: 'flex', gap: 20, flexWrap: 'wrap' }}>
            <div>
              <div style={{ fontSize: 12, color: '#888', fontWeight: 600, marginBottom: 6 }}>FRONT</div>
              <canvas ref={frontRef} width={W} height={H} style={{ width: 240, height: 373, borderRadius: 12, boxShadow: '0 4px 16px rgba(0,0,0,0.18)' }} />
            </div>
            <div>
              <div style={{ fontSize: 12, color: '#888', fontWeight: 600, marginBottom: 6 }}>BACK</div>
              <canvas ref={backRef} width={W} height={H} style={{ width: 240, height: 373, borderRadius: 12, boxShadow: '0 4px 16px rgba(0,0,0,0.18)' }} />
            </div>
          </div>
        </div>

      </div>
    </>
  );
}
